import React, { useState, useEffect } from "react";
import { X } from 'lucide-react';
import { useSelector } from "react-redux";
import Lang from "../../config/lang";

const Drawer = ({ title = 'Menu', description = null, job = {}, isOpen, onClose, position = 'left', setWidth = 'w-80', children, setStyle = {} }) => {
  const { lang } = useSelector((state) => state.globalReducer);
    const [lockedDrawerHeight, setLockedDrawerHeight] = useState(null);
        const [keyboardInset, setKeyboardInset] = useState(0);
        const [activeKeyboardInset, setActiveKeyboardInset] = useState(0);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    useEffect(() => {
        if (!isOpen) return;

        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        setLockedDrawerHeight(`${Math.round(viewportHeight)}px`);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            setKeyboardInset(0);
            return;
        }

        const updateKeyboardInset = () => {
            if (!window.visualViewport) {
                setKeyboardInset(0);
                return;
            }

            const { height, offsetTop } = window.visualViewport;
            const inset = Math.max(0, window.innerHeight - (height + offsetTop));
            setKeyboardInset(inset);
        };

        updateKeyboardInset();

        window.visualViewport?.addEventListener('resize', updateKeyboardInset);
        window.visualViewport?.addEventListener('scroll', updateKeyboardInset);

        return () => {
            window.visualViewport?.removeEventListener('resize', updateKeyboardInset);
            window.visualViewport?.removeEventListener('scroll', updateKeyboardInset);
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            setActiveKeyboardInset(0);
            return;
        }

        const handleFocusIn = (event) => {
            const target = event.target;
            const isFocusableField = target instanceof HTMLElement && (
                target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.tagName === 'SELECT' ||
                target.isContentEditable
            );

            if (!isFocusableField) return;

            setActiveKeyboardInset(keyboardInset);
            requestAnimationFrame(() => {
                target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
        };

        const handleFocusOut = () => {
            setTimeout(() => {
                const activeElementTag = document.activeElement?.tagName;
                const stillOnField = ['INPUT', 'TEXTAREA', 'SELECT'].includes(activeElementTag) || document.activeElement?.isContentEditable;

                if (!stillOnField) {
                    setActiveKeyboardInset(0);
                }
            }, 120);
        };

        document.addEventListener('focusin', handleFocusIn);
        document.addEventListener('focusout', handleFocusOut);

        return () => {
            document.removeEventListener('focusin', handleFocusIn);
            document.removeEventListener('focusout', handleFocusOut);
        };
    }, [isOpen, keyboardInset]);

    const getDrawerClasses = () => {
        const baseClasses = 'fixed top-0 bg-white shadow-xl z-50 transition-transform duration-300 ease-in-out';

        switch (position) {
            case 'right':
                return `${baseClasses} right-0 ${setWidth} ${isOpen ? 'translate-x-0' : 'translate-x-full'} rounded-l-xl`;
            case 'left':
            default:
                return `${baseClasses} left-0 ${setWidth} ${isOpen ? 'translate-x-0' : '-translate-x-full'} rounded-r-xl`;
        }
    };

    return (<>
        <div
            className={`fixed inset-0 ${isOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}
            style={{ zIndex: 10000 }}
        >
            <div
                onClick={onClose}
                className={`absolute inset-0 bg-black transition-opacity duration-300 ease-in-out ${isOpen ? 'opacity-50' : 'opacity-0'}`}
            />
            <div
                className={getDrawerClasses()}
                style={{
                    ...setStyle,
                    height: lockedDrawerHeight || '100dvh',
                    maxHeight: lockedDrawerHeight || '100dvh'
                }}
            >
                <div className="flex flex-col gap-6 h-full">
                    {/* Header */}
                    <div className="flex items-center justify-between pt-6 px-6 md:pt-6 md:px-8">
                        <h2 className="text-lg md:text-[26px] font-semibold text-gray-800">{title}</h2>
                        <button onClick={onClose} className="hover:bg-gray-100 rounded-lg transition-colors" aria-label="Close drawer">
                            <X size={32} color="#48494B" />
                        </button>
                    </div>
                    {description !== null && (
                        <div className="px-6 md:px-8">
                            {description}
                        </div>
                    )}
                    
                    {/* <p className='text-[11px] md:text-xl font-bold '>{title}</p>
                    {job.tags.split(',').map((val, idx) => (
                        <div key={idx} className='py-1 px-3 mr-3 rounded-xl md:rounded-full bg-[#E4F0FF]'>
                        </div>
                    ))} */}

                    {/* Content */}
                    <div
                        className="flex-1 overflow-y-auto px-6 md:px-8"
                        style={{ paddingBottom: activeKeyboardInset > 0 ? `${activeKeyboardInset + 24}px` : undefined }}
                    >
                        {children}
                    </div>
                </div>
            </div>
        </div>
    </>)
}

export default Drawer;